import { computed, inject, type ComputedRef } from 'vue'
import type { OrganizationSettings } from '@lib/types/organizationSettings.types'
import type { InstanceSettings } from '@lib/types/instanceSettings.types'

interface NameDescription {
  name: string
  description: string
}

/**
 * Composable for parsing folder/file names with conditional description support
 *
 * When `enable_folder_description_via_name` setting is enabled:
 * - Names like "Folder name | Folder description" are parsed
 * - Returns { name: "Folder name", description: "Folder description" }
 *
 * When setting is disabled:
 * - Returns original name without parsing
 * - Returns { name: "Full name", description: "" }
 */
export default function useFolderNameDescription() {
  const pitcherSettings = inject<ComputedRef<OrganizationSettings & InstanceSettings>>(
    'pitcherSettings',
    computed(() => ({})),
  )

  const isEnabled = computed(() => !!pitcherSettings.value?.enable_folder_description_via_name)

  /**
   * Parse a name string into name and description parts
   */
  function parseNameDescription(nameString: string): NameDescription {
    if (!isEnabled.value) {
      return { name: nameString, description: '' }
    }

    const idx = nameString.indexOf('|')
    if (idx === -1) {
      return { name: nameString.trim(), description: '' }
    }

    return {
      name: nameString.slice(0, idx).trim(),
      description: nameString.slice(idx + 1).trim(),
    }
  }

  /**
   * Create a reactive computed property for name description parsing
   */
  function createNameDescription(nameRef: ComputedRef<string>) {
    return computed(() => parseNameDescription(nameRef.value))
  }

  /**
   * Create a reactive computed property for name description parsing that only applies to folders
   * For non-folder items, returns the original name without parsing
   */
  function createNameDescriptionForItem(nameRef: ComputedRef<string>, itemTypeRef: ComputedRef<string>) {
    return computed(() => {
      if (itemTypeRef.value === 'folder') {
        return parseNameDescription(nameRef.value)
      }
      return { name: nameRef.value, description: '' }
    })
  }

  /**
   * Parse only the name part (useful for breadcrumbs and simple displays)
   */
  function parseNameOnly(nameString: string): string {
    return parseNameDescription(nameString).name
  }

  return {
    isEnabled,
    isFolderNameDescriptionEnabled: isEnabled,
    parseNameDescription,
    createNameDescription,
    createNameDescriptionForItem,
    parseNameOnly,
  }
}
